import { Component, effect, signal } from '@angular/core';
import { TranslocoModule, TranslocoPipe } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NgApexchartsModule, ApexOptions } from 'ng-apexcharts';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { LegacyContractStatusEnum, SalesAdminDashboardContractsDto, SalesDashboardService, SalesDashboardSummaryResponse } from './sales-dashboard.service';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-sales-dashboard',
    templateUrl: './sales-dashboard.component.html',
    styleUrls: ['./sales-dashboard.component.css'],
    imports: [
        TranslocoModule,
        MatIconModule,
        MatButtonModule,
        MatRippleModule,
        MatMenuModule,
        MatTabsModule,
        MatButtonToggleModule,
        NgApexchartsModule,
        MatTableModule,
        DatePipe,
        TranslocoPipe
    ]
})

export class SalesDashboardComponent {
    chartGithubIssues: ApexOptions = {};
    data = signal<SalesDashboardSummaryResponse>({
        reservedVehicles: [],
        contractsInProgress: [],
        soldCarsThisWeek: [],
        pendingDeliveries: 0,
        userPendingDeliveries: 0,
        totalContracts: [],
        schedules: [],
    });

    get soldCarsPeriod() {
        switch (this.soldCarsTimePeriod()) {
            case 'Last 7 Days':
                return "Last7Days";
            case 'Last 30 Days':
                return "Last30Days";
            case 'Last 3 Months':
                return "Last3Months";
            case 'Last 6 Months':
                return "Last6Months";
            case 'Last 9 Months':
                return "Last9Months";
            case 'Last Year':
                return "LastYear";
            default:
                return "Last30Days";
        }
    }

    scheduleDaySelector = signal<string>("Today"); // or 'Tomorrow'
    soldCarsTimePeriod = signal<string>("Last 7 Days");
    selectedTabIndex: number = 0;

    constructor(
        public authService: AuthService,
        private _salesDashboardService: SalesDashboardService,
        private _router: Router,
    ) {
        effect(() => {
            console.log("parameter changed")
            this.getDashboardData();
        });
    }

    getDashboardData() {
        this._salesDashboardService.getSalesDashboardData(this.scheduleDaySelector(), this.soldCarsTimePeriod()).subscribe({
            next: (response: any) => {
                this.data.set(response.value);
                console.log(this.data());
                this._prepareChartData();
            },
            error: (error) => {
                console.error('Error fetching dashboard data:', error);
            },
        });

        window['Apex'] = {
            chart: {
                events: {
                    mounted: (chart: any, options?: any): void => {
                        this._fixSvgFill(chart.el);
                    },
                    updated: (chart: any, options?: any): void => {
                        this._fixSvgFill(chart.el);
                    },
                },
            },
        };
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    changeSoldCarsPeriod() {
        console.log("sold cars period:" + this.soldCarsTimePeriod);
        this.getDashboardData();
    }

    private _fixSvgFill(element: Element): void {
        const currentURL = this._router.url;
        Array.from(element.querySelectorAll('*[fill]'))
            .filter((el) => el.getAttribute('fill').indexOf('url(') !== -1)
            .forEach((el) => {
                const attrVal = el.getAttribute('fill');
                el.setAttribute(
                    'fill',
                    `url(${currentURL}${attrVal.slice(attrVal.indexOf('#'))}`
                );
            });
    }

    private _prepareChartData() {
        const { active, completed } = this._groupContractsByWeek(this.data().totalContracts);

        this.chartGithubIssues = {
            chart: {
                fontFamily: 'inherit',
                foreColor: 'inherit',
                height: '100%',
                type: 'line',
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: false,
                },
            },
            colors: ['#64748B', '#05cb55'],
            dataLabels: {
                enabled: true,
                enabledOnSeries: [0],
                background: {
                    borderWidth: 0,
                },
            },
            grid: {
                borderColor: 'var(--fuse-border)',
            },
            labels: this.contractChartLabels,
            legend: {
                show: false,
            },
            plotOptions: {
                bar: {
                    columnWidth: '50%',
                },
            },
            series: [
                {
                    name: 'Active Contracts',
                    type: 'line',
                    data: active,
                },
                {
                    name: 'Completed Contracts',
                    type: 'column',
                    data: completed,
                },
            ],
            states: {
                hover: {
                    filter: {
                        type: 'darken',
                    },
                },
            },
            stroke: {
                width: [3, 0],
            },
            tooltip: {
                followCursor: true,
                theme: 'dark',
            },
            xaxis: {
                axisBorder: {
                    show: false,
                },
                axisTicks: {
                    color: 'var(--fuse-border)',
                },
                labels: {
                    style: {
                        colors: 'var(--fuse-text-secondary)',
                    },
                },
                tooltip: {
                    enabled: false,
                },
            },
            yaxis: {
                labels: {
                    offsetX: -16,
                    style: {
                        colors: 'var(--fuse-text-secondary)',
                    },
                },
            },
        };
    }

    private _groupContractsByWeek(contracts: SalesAdminDashboardContractsDto[]) {
        const now = new Date();

        // Create 4 weekly ranges (from 30 days ago to today)
        const weekRanges = Array.from({ length: 4 }).map((_, i) => {
            const end = new Date(now);
            end.setDate(now.getDate() - i * 7);
            const start = new Date(end);
            start.setDate(end.getDate() - 6);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return { start, end };
        }).reverse(); // oldest week first

        const active: number[] = [0, 0, 0, 0];
        const completed: number[] = [0, 0, 0, 0];

        for (const contract of contracts) {
            const contractDate = new Date(contract.date);

            for (let i = 0; i < weekRanges.length; i++) {
                const { start, end } = weekRanges[i];
                if (contractDate >= start && contractDate <= end) {
                    if (contract.status != LegacyContractStatusEnum.Completed) {
                        active[i]++;
                    } else if (contract.status === LegacyContractStatusEnum.Completed) {
                        completed[i]++;
                    }
                    break; // stop once matched
                }
            }
        }

        return { active, completed };
    }


    get getReservedVehiclesCount(): number {
        return this.data().reservedVehicles?.length ?? 0;
    }
    get getContractsWithNoDocs(): number {
        return this.data().contractsInProgress.filter(x => x.awaitingDocs).length ?? 0;
    }
    get getContractsInProgressCount(): number {
        return this.data().contractsInProgress?.length ?? 0;
    }
    get getSoldCarsCount(): number {
        return this.data().soldCarsThisWeek?.length ?? 0;
    }
    get getDeliveredCarsCount(): number {
        return this.data().soldCarsThisWeek.filter(x => x.delivered).length ?? 0;
    }
    get getTotalContractsCount(): number {
        console.log(this.data().totalContracts?.length);
        return this.data().totalContracts?.length ?? 0;
    }
    get getSchedulesCount(): number {
        return this.data().schedules?.length ?? 0;
    }
    get getPendingDeliveriesCount(): number {
        return this.data().pendingDeliveries ?? 0;
    }
    get getUserPendingDeliveriesCount(): number {
        return this.data().userPendingDeliveries ?? 0;
    }

    // 🆕 Label getter
    get contractChartLabels(): string[] {
        return ['Week 1', 'Week 2', 'Week 3', 'Week 4']; // Week 1 = most recent
    }
}